import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { FiCalendar, FiClock, FiChevronLeft, FiChevronRight, FiThumbsUp, FiArrowLeft } from 'react-icons/fi'
import { RootState, AppDispatch } from '@/store/store'
import {
    fetchByCategory,
    setCurrentPage,
    setActiveFilters,
    likeBlog
} from '@/store/slices/blogSlice'
import { IBlogPost } from '@/types/blog'
import { formatBlogDate } from '@/utils/blogHelpers'

const BlogCategoryPage: React.FC = () => {
    const { category } = useParams<{ category: string }>()
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    const {
        posts,
        isLoading,
        pagination,
        activeFilters,
        likedPosts
    } = useSelector((state: RootState) => state.blog)

    // Fetch posts for this category on mount
    useEffect(() => {
        if (category) {
            dispatch(fetchByCategory({
                category: decodeURIComponent(category),
                filters: { page: 1, limit: 10 }
            }))
        }
    }, [category, dispatch])

    // Handle pagination
    const handlePageChange = (newPage: number) => {
        if (category) {
            dispatch(setCurrentPage(newPage))
            const filters = { ...activeFilters, page: newPage }
            dispatch(setActiveFilters(filters))
            dispatch(fetchByCategory({
                category: decodeURIComponent(category),
                filters
            }))
        }
    }

    // Handle like
    const handleLike = (postId: string) => {
        dispatch(likeBlog(postId))
    }

    // Navigate to post detail
    const handleReadMore = (post: IBlogPost) => {
        navigate(`/blog/${post.slug}`)
    }

    // Helper function to extract category name (handle both string and object formats)
    const getCategoryName = (cat: any): string => {
        if (typeof cat === 'string') return cat
        if (typeof cat === 'object' && cat?.name) return cat.name
        return ''
    }

    const categoryTitle = category ? decodeURIComponent(category).charAt(0).toUpperCase() + decodeURIComponent(category).slice(1) : 'Category'

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/blog')}
                        className="flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-4 font-medium transition-colors"
                    >
                        <FiArrowLeft className="w-4 h-4" />
                        Back to Blog
                    </button>
                    <h1 className="heading-section text-gray-900 dark:text-white mb-2">
                        {categoryTitle}
                    </h1>
                    <p className="text-body text-gray-600 dark:text-gray-400">
                        Showing {posts.length} {posts.length === 1 ? 'article' : 'articles'} in {categoryTitle}
                    </p>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="flex justify-center items-center min-h-96">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-primary-600"></div>
                    </div>
                )}

                {/* Empty State */}
                {!isLoading && posts.length === 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-12 text-center">
                        <h2 className="heading-md text-gray-900 dark:text-white mb-3">
                            No articles found
                        </h2>
                        <p className="text-body text-gray-600 dark:text-gray-400 mb-6">
                            There are no blog articles in the {categoryTitle} category yet.
                        </p>
                        <button
                            onClick={() => navigate('/blog')}
                            className="btn-primary"
                        >
                            Browse All Articles
                        </button>
                    </div>
                )}

                {/* Posts Grid */}
                {!isLoading && posts.length > 0 && (
                    <>
                        <div className="grid gap-6 mb-8">
                            {posts.map((post) => (
                                <article
                                    key={post._id}
                                    className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                                >
                                    <div className="flex flex-col sm:flex-row gap-6 p-6">
                                        {/* Image */}
                                        {post.heroImage && (
                                            <div className="sm:w-48 sm:h-48 flex-shrink-0">
                                                <img
                                                    src={post.heroImage}
                                                    alt={post.heroImageAlt || post.title}
                                                    className="w-full h-full object-cover rounded-lg"
                                                />
                                            </div>
                                        )}

                                        {/* Content */}
                                        <div className="flex-1 flex flex-col justify-between">
                                            {/* Title and Excerpt */}
                                            <div>
                                                <div className="flex items-center gap-3 mb-3">
                                                    {post.featured && (
                                                        <span className="inline-block px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs font-semibold rounded-full">
                                                            Featured
                                                        </span>
                                                    )}
                                                    <span className="inline-block px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 text-xs font-semibold rounded-full">
                                                        {getCategoryName(post.category)}
                                                    </span>
                                                </div>
                                                <h2
                                                    className="heading-md text-gray-900 dark:text-white mb-2 cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                                                    onClick={() => handleReadMore(post)}
                                                >
                                                    {post.title}
                                                </h2>
                                                <p className="text-body text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                                                    {post.excerpt}
                                                </p>
                                            </div>

                                            {/* Meta Info */}
                                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                                                <div className="flex items-center gap-1">
                                                    <FiCalendar className="w-4 h-4" />
                                                    <span>{formatBlogDate(post.publishedAt)}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <FiClock className="w-4 h-4" />
                                                    <span>{post.readingTime} min read</span>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-4">
                                                <button
                                                    onClick={() => handleLike(post._id)}
                                                    className={`flex items-center gap-2 text-sm font-medium transition-colors ${likedPosts.includes(post._id)
                                                        ? 'text-primary-600 dark:text-primary-400'
                                                        : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
                                                        }`}
                                                >
                                                    <FiThumbsUp className="w-4 h-4" />
                                                    <span>{post.likes}</span>
                                                </button>
                                                <button
                                                    onClick={() => handleReadMore(post)}
                                                    className="ml-auto text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
                                                >
                                                    Read More →
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="flex justify-center items-center gap-2 mt-12">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                    aria-label="Go to previous page"
                                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <FiChevronLeft className="w-5 h-5" />
                                </button>

                                <div className="flex gap-2">
                                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                                        <button
                                            key={page}
                                            onClick={() => handlePageChange(page)}
                                            className={`px-3 py-2 rounded-lg font-medium transition-colors ${pagination.currentPage === page
                                                ? 'bg-primary-600 text-white'
                                                : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                }`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    aria-label="Go to next page"
                                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <FiChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default BlogCategoryPage